import { useTinyHost } from "@tiny/host-contract";
import React from "react";

import MoreHorizontal from "@tiny/icons/generated/MoreHorizontal";
import ChevronBigDown from "@tiny/icons/generated/ChevronBigDown";
import ChevronBigUp from "@tiny/icons/generated/ChevronBigUp";
import CleaningService from "@tiny/icons/generated/CleaningService";
import {
  Grid,
  Checkbox,
  Typography,
  IconButton,
  Menu,
  MenuItem,
  Collapse,
  Link,
  TruncateTooltip,
  Chip,
  Box,
  OverFlowTip,
  Tooltip,
} from "@tiny/components";
import ItemActionTimeInfo from "@tiny/components/src/ItemActionTimeInfo/ItemActionTimeInfo";
import useBreakpoints from "@tiny/components/src/hooks/useBreakpoints";
import { SegmentView } from "@tiny/api";
import { useAudienceRow } from "./AudienceRow.hooks";
import { dateFormat } from "./AudienceRow.helper";
import { AudienceRowProps } from "./AudienceRow.types";
import { useStyles } from "./AudienceRow.styles";
import { TinyImporter } from "../../TinyImportDialog";
import { useAudienceListView } from "../AudienceList.context";
import { formatNumber } from "@tiny/components/src/utils/formatUtils";
import CleanedListHistoryDialog from "../../CleanedListHistoryDialog";

// Product IDs for Clean List feature
const CLEAN_LIST_PRODUCT_IDS = {
  STAGE: "ff2d8ca0-8019-4a80-b317-3428ab04553b",
  PROD: "6df2e7bd-7687-4221-ac62-acf85f45e39b",
};

function AudienceRow(props: AudienceRowProps) {
  const cleanListProductId = useTinyHost().capabilities.isStagingEnvironment
    ? CLEAN_LIST_PRODUCT_IDS.STAGE
    : CLEAN_LIST_PRODUCT_IDS.PROD;
  const { navigate, slots } = useTinyHost();
  const { audience } = props;

  const { mdUp } = useBreakpoints();

  const classes = useStyles();

  const {
    onSelect,
    subscribersText,
    disabledCheckbox,
    disabledCheckboxToolTip,
    sortAction,
    timeActions,
    chipColor,
    chipText,
    moreActions,
    detailsOpen,
    moreButtonRef,
    checked,
    moreOpen,
    toggleDetails,
    showMore,
    hideMore,
    mutationLoading,
    audienceLocation,
    tinyImporterOpen,
    closeTinyImporter,
    openTinyImporter,
    statusMap,
    cleanedHistoryDialogOpen,
    closeCleanedHistoryDialog,
    productDetailsDialogOpen,
    closeProductDetailsDialog,
  } = useAudienceRow(props);

  const { reloadAudiences } = useAudienceListView();

  let audienceName: React.ReactElement;
  if (mdUp) {
    audienceName = (
      <TruncateTooltip arrow>
        <Typography
          variant="label1"
          mr={2}
          className={classes.name}
          noWrap
          color="primary"
          tone="main"
        >
          {audience.name}
        </Typography>
      </TruncateTooltip>
    );
  } else {
    audienceName = (
      <Typography
        variant="h4"
        mr={2}
        color="primary"
        tone="main"
        component="span"
      >
        {audience.name && <OverFlowTip text={audience.name} />}
      </Typography>
    );
  }

  const renderAudienceName = () => {
    if (
      audience?.status === "COMPLETED" ||
      audience?.status === "VERIFICATION"
    ) {
      return (
        <Link
          component={"button" as React.ElementType}
          onClick={() =>
            navigate.to(audienceLocation.pathname, {
              state: audienceLocation.state,
            })
          }
        >
          {audienceName}
        </Link>
      );
    }
    return <Link>{audienceName}</Link>;
  };

  return (
    <li className={classes.root}>
      <div className={classes.top}>
        <div className={classes.check}>
          <Checkbox
            color="primary"
            checked={checked}
            onChangeValue={onSelect}
            disabled={
              disabledCheckbox ||
              (audience?.status !== "COMPLETED" &&
                audience?.status !== "VERIFICATION")
            }
            disabledTooltip={disabledCheckboxToolTip}
          />
        </div>
        <div className={classes.main}>
          {mdUp ? (
            <>
              {renderAudienceName()}
              <Chip label={chipText} color={chipColor} size="small" />
              <ItemActionTimeInfo
                actions={timeActions}
                dateFormat={dateFormat}
                highlight={sortAction}
                display="inline"
              />
            </>
          ) : (
            <Box display="flex" flexDirection="column">
              <Box
                minWidth={0}
                display="flex"
                flexDirection="row"
                height={20}
                position="relative"
              >
                {renderAudienceName()}
              </Box>
              <Box
                minWidth={0}
                display="flex"
                flexDirection="row"
                alignItems="center"
              >
                <Chip label={chipText} color={chipColor} size="small" />
                <ItemActionTimeInfo
                  actions={timeActions}
                  dateFormat={dateFormat}
                  highlight={sortAction}
                  display="inline"
                />
                <IconButton
                  onClick={toggleDetails}
                  className={classes.detailsButton}
                  size="large"
                >
                  {detailsOpen ? <ChevronBigUp /> : <ChevronBigDown />}
                </IconButton>
              </Box>
            </Box>
          )}
        </div>
        {mdUp && (
          <div className={classes.details}>
            <Details
              item={audience}
              subscribersText={subscribersText}
              statusMap={statusMap}
            />
          </div>
        )}
        <div className={classes.more}>
          {moreActions.length > 0 && (
            <IconButton
              ref={moreButtonRef}
              className={classes.moreButton}
              onClick={showMore}
              disabled={disabledCheckbox}
              size="large"
            >
              <MoreHorizontal />
            </IconButton>
          )}
          <Menu
            anchorEl={moreButtonRef.current}
            keepMounted
            open={moreOpen}
            onClose={hideMore}
            disableScrollLock
          >
            {moreActions.map((action) => (
              <MenuItem
                key={action.name}
                onClick={() => {
                  hideMore();
                  action.onClickAction(audience.id!);
                }}
                disabled={mutationLoading}
              >
                {action.name}
              </MenuItem>
            ))}
          </Menu>
        </div>
      </div>
      {!mdUp && (
        <Collapse in={detailsOpen}>
          <div className={classes.bottom}>
            <Details
              item={audience}
              subscribersText={subscribersText}
              statusMap={statusMap}
            />
          </div>
        </Collapse>
      )}
      <TinyImporter
        open={tinyImporterOpen}
        onClose={closeTinyImporter}
        value={audience?.name}
        segmentDetails={audience as never}
        onSuccess={() => {
          closeTinyImporter();
          reloadAudiences();
        }}
      />
      <CleanedListHistoryDialog
        open={cleanedHistoryDialogOpen}
        onClose={closeCleanedHistoryDialog}
        segmentId={audience.id || ""}
      />
      {slots?.productDetailsDialog?.({
        open: productDetailsDialogOpen,
        onClose: closeProductDetailsDialog,
        productId: cleanListProductId,
        segmentId: audience.id || "",
        onSuccess: reloadAudiences,
      })}
    </li>
  );
}

export interface DetailsProps {
  item: SegmentView;
  subscribersText: string;
  statusMap: { [key: string]: string };
}

function Details(props: DetailsProps) {
  const { item, subscribersText, statusMap } = props;
  return (
    <Grid container verticalSpacing={3}>
      <Grid item xs={6} md={3}>
        <Typography
          variant="body2"
          color="grey"
          tone={900}
          component="div"
          mb={1}
        >
          {formatNumber(item.numberOfCustomers)}
        </Typography>
        <Typography variant="label3" color="grey" tone={600} component="div">
          {subscribersText}
        </Typography>
      </Grid>
      <Grid item xs={6} md={3}>
        <Typography
          variant="body2"
          color="grey"
          tone={900}
          component="div"
          mb={1}
        >
          {item.assignedTo?.length === 0 ? "No" : "Yes"}
        </Typography>
        <Typography variant="label3" color="grey" tone={600} component="div">
          Assigned?
        </Typography>
      </Grid>
      <Grid item xs={6} md={3}>
        <Typography
          variant="body2"
          color="grey"
          tone={900}
          component="div"
          mb={1}
        >
          {statusMap[item?.status || ""] || "-"}
          <Typography variant="label3" color="grey" tone={600} component="div">
            Status
          </Typography>
        </Typography>
      </Grid>
      <Grid item xs={6} md={3}>
        {item?.status === "VERIFICATION" && (
          <>
            <Box display="flex" alignItems="center" height="100%">
              <Tooltip title="List cleaning in progress" arrow>
                <CleaningService
                  style={{ width: 20, height: 20, color: "#9c27b0" }}
                />
              </Tooltip>
            </Box>
          </>
        )}
        {item?.status === "COMPLETED" && item?.cleaned && (
          <>
            <Tooltip title="Cleaning Finished" arrow>
              <CleaningService
                style={{ width: 20, height: 20, color: "#008060" }}
              />
            </Tooltip>
          </>
        )}
      </Grid>
    </Grid>
  );
}

export default React.memo(AudienceRow);
